/**
 * Dr. Mani's Magic Quiz — logic engine.
 *
 * Mirrors dr-mm-files/dmm-quiz-spec.json -> logic, evaluated in the spec's
 * "first matching rule wins, in order" order. Resolves archetype + profile,
 * derives the need, assembles the regimen (with modifiers + dosing), and
 * builds the Klaviyo dmm_quiz_* payload.
 */

import type {
  Archetype,
  Profile,
  KeyInsight,
  NeedKey,
  ProductKey,
  AddOnKey,
  RegimenItem,
  RegimenAddOn,
  ResolvedRegimen,
  DmmProductSizing,
  DmmSegmentKey,
  DmmSegmentReview,
} from '../data/drManiQuiz/types';
import {
  DMM_ARCHETYPES,
  DMM_PROFILES,
  DMM_KEY_INSIGHTS,
  DMM_NEED_DIAGNOSIS,
  DMM_SEGMENT_MORSELS,
  DMM_PAIN_VILLAIN,
  DMM_PILLAR_SNIPPETS,
} from '../data/drManiQuiz/library';
import {
  DMM_NEED_TO_REGIMEN,
  DMM_PROFILE_NEED,
  DMM_KEY_INSIGHT_BY_WHY,
} from '../data/drManiQuiz/logic';
import { DMM_DOSING } from '../data/drManiQuiz/dosing';
import { DMM_PRODUCTS, DMM_ADDON_PRODUCTS, DMM_OFFER } from '../data/drManiQuiz/brand';
import { DMM_QUESTIONS } from '../data/drManiQuiz/questions';
import {
  DMM_ADDON_BLURBS,
  DMM_PRODUCT_BLURBS,
  DMM_LAWN_SIZING,
  DMM_SEGMENT_BY_GROW,
  DMM_SEGMENT_PRIORITY,
  DMM_SEGMENT_REVIEWS,
  DMM_SIZING_BY_SIGNAL,
  getDmmVariant,
} from '../data/drManiQuiz/sizing';
import {
  DMM_BECAUSE_STRUGGLE,
  DMM_BECAUSE_PROFILE,
  DMM_BECAUSE_LAWN,
  DMM_BECAUSE_CONTAINERS,
  DMM_BECAUSE_GROW_FOOD,
  DMM_BECAUSE_NATURAL,
  DMM_BECAUSE_FALLBACK,
  dmmBecauseLine,
  dmmBecauseLineStacked,
  type DmmBecauseTemplate,
} from '../data/drManiQuiz/uxCopy';

/** Raw answers keyed by the spec's writes_field names. Single-select = string, multi = string[]. */
export interface DmmAnswers {
  dmm_quiz_living?: string;
  dmm_quiz_grows?: string[];
  dmm_quiz_setup?: string[];
  dmm_quiz_lawn_size?: string;
  dmm_quiz_size?: string;
  dmm_quiz_why?: string;
  dmm_quiz_struggle?: string[];
  dmm_quiz_experience?: string;
  dmm_quiz_proudest?: string;
}

/** Side-effects accumulated from option `sets` (has_lawn, primary_setup, scale) + setup environment. */
export interface DmmDerivedState {
  has_lawn: boolean;
  primary_setup?: string | undefined;
  scale?: string | undefined;
  environment: 'indoor' | 'outdoor' | 'mixed';
  setup_adds: string[];
}

/** q7 struggle value -> need category, for the "spans 2+ categories" profile rule. */
const STRUGGLE_NEED_CATEGORY: Record<string, NeedKey | 'PEST' | 'PROOF'> = {
  rot_soggy: 'HOME',
  yellow_pale: 'FOOD',
  no_bloom_fruit: 'FOOD',
  plants_die: 'LIFE',
  bugs_flies: 'PEST',
  smell: 'PROOF',
  chemicals: 'PROOF',
  ugly_mix: 'HOME',
  no_directions: 'PROOF',
  lawn_rings: 'LIFE',
};

const arr = (v: string[] | undefined): string[] => (Array.isArray(v) ? v : v ? [v] : []);
const includesAny = (list: string[], targets: string[]): boolean => targets.some((t) => list.includes(t));

function findArchetypeByName(name: string): Archetype {
  return DMM_ARCHETYPES.find((a) => a.name === name) || DMM_ARCHETYPES[DMM_ARCHETYPES.length - 1];
}
function findProfileByName(name: string): Profile {
  const profile = DMM_PROFILES.find((candidate) => candidate.name === name);
  // Why this matters: a missing library entry is a configuration bug, not a reason to return a different diagnosis.
  if (!profile) {
    throw new Error(`DMM profile "${name}" is not defined in DMM_PROFILES`);
  }
  return profile;
}

/**
 * Resolve the archetype per logic.archetype_selection (first match wins).
 * Why order matters: the spec lists rules in priority order; the first rule
 * whose condition holds wins, so Brave Beginner outranks Comeback Grower, etc.
 */
export function resolveArchetype(answers: DmmAnswers, state: DmmDerivedState): Archetype {
  const grows = arr(answers.dmm_quiz_grows);
  const struggle = arr(answers.dmm_quiz_struggle);
  const experience = answers.dmm_quiz_experience;

  // Rule 1: experience == 'new' OR grows includes 'just_starting'
  if (experience === 'new' || grows.includes('just_starting')) {
    return findArchetypeByName('The Brave Beginner');
  }
  // Rule 2: struggle includes 'plants_die' AND experience in [getting_hang, experienced, expert]
  if (struggle.includes('plants_die') && ['getting_hang', 'experienced', 'expert'].includes(experience || '')) {
    return findArchetypeByName('The Comeback Grower');
  }
  // Rule 3: living == 'apartment' OR primary setup is containers/indoors
  if (
    answers.dmm_quiz_living === 'apartment' ||
    state.primary_setup === 'containers' ||
    state.primary_setup === 'indoors'
  ) {
    return findArchetypeByName('The Small-Space Master');
  }
  // Rule 4: why == 'natural' (also the archetype_override on the q6 'natural' option)
  if (answers.dmm_quiz_why === 'natural') {
    return findArchetypeByName('The Purist');
  }
  // Rule 5: else by primary grows (first grows selection that maps to an archetype)
  const primaryGrowsMap: Record<string, string> = {
    vegetables: 'The Provider',
    herbs: 'The Provider',
    flowers: 'The Color Bringer',
    pollinator: 'The Color Bringer',
    lawn: 'The Groundskeeper',
    houseplants: 'The Plant Parent',
    fruit_trees: 'The Orchard Keeper',
    berries_grapes: 'The Orchard Keeper',
    trees_shrubs: 'The Orchard Keeper',
    succulents: 'The Curator',
  };
  for (const g of grows) {
    if (primaryGrowsMap[g]) return findArchetypeByName(primaryGrowsMap[g]);
  }
  // Rule 6: fallback
  return findArchetypeByName('The Provider');
}

/**
 * Resolve the profile per logic.profile_selection (first match wins).
 * "struggle spans 2+ distinct need categories (HOME/FOOD/LIFE)" counts distinct
 * categories among the checked struggles, restricted to HOME/FOOD/LIFE.
 */
export function resolveProfile(answers: DmmAnswers): Profile {
  const grows = arr(answers.dmm_quiz_grows);
  const struggle = arr(answers.dmm_quiz_struggle);
  const experience = answers.dmm_quiz_experience;

  // Rule 1: grows includes 'just_starting' OR struggle spans 2+ distinct need categories (HOME/FOOD/LIFE)
  const needCats = new Set(
    struggle.map((s) => STRUGGLE_NEED_CATEGORY[s]).filter((c): c is NeedKey => c === 'HOME' || c === 'FOOD' || c === 'LIFE')
  );
  // Why this matters: profile names must match library.ts or strict lookup reports the broken mapping.
  if (grows.includes('just_starting') || needCats.size >= 2) {
    return findProfileByName('The Whole-Garden Builder');
  }
  // Rule 2: struggle includes 'plants_die' AND experience in [experienced, expert]
  if (struggle.includes('plants_die') && ['experienced', 'expert'].includes(experience || '')) {
    return findProfileByName('The Reviver');
  }
  // Rule 3: struggle includes any of [rot_soggy, ugly_mix, gnats]
  if (includesAny(struggle, ['rot_soggy', 'ugly_mix', 'gnats'])) {
    return findProfileByName('The Root Rescuer');
  }
  // Rule 4: struggle includes any of [yellow_pale]
  if (struggle.includes('yellow_pale')) {
    return findProfileByName('The Nourisher');
  }
  // Rule 5: struggle includes any of [no_bloom_fruit]
  if (struggle.includes('no_bloom_fruit')) {
    return findProfileByName('The Bloom Chaser');
  }
  // Rule 6: struggle includes 'plants_die' OR 'lawn_rings'
  if (struggle.includes('plants_die') || struggle.includes('lawn_rings')) {
    return findProfileByName('The Soil Awakener');
  }
  // Fallback
  return findProfileByName('The Whole-Garden Builder');
}

/** Pick a dosing rule line for a product given the user's setup/environment. */
function doseForProduct(key: ProductKey, answers: DmmAnswers, state: DmmDerivedState): string {
  const setup = arr(answers.dmm_quiz_setup);
  const isLawn = arr(answers.dmm_quiz_grows).includes('lawn');
  const isIndoor = state.environment === 'indoor';

  if (key === 'crab_kelp_amino_acids') {
    if (isLawn) return DMM_DOSING.crab_kelp_amino_acids.rules.lawns;
    if (setup.includes('houseplants_indoor') || isIndoor) return DMM_DOSING.crab_kelp_amino_acids.rules.indoor_plants;
    if (setup.includes('in_ground') || setup.includes('raised_beds')) {
      return `${DMM_DOSING.crab_kelp_amino_acids.rules.gardens_beds_at_planting} (at planting); ${DMM_DOSING.crab_kelp_amino_acids.rules.gardens_beds_established} (established)`;
    }
    return DMM_DOSING.crab_kelp_amino_acids.rules.trees_and_outdoor_pots;
  }
  if (key === 'plant_super_boost') {
    const lawnSizeHint = optionHint(answers, 'dmm_quiz_lawn_size', 'psb');
    const base = DMM_DOSING.plant_super_boost.rules.all_plants;
    return isLawn && lawnSizeHint ? `${base} (${lawnSizeHint} for your lawn size)` : base;
  }
  if (key === 'super_soil') {
    const sizeHint = optionHint(answers, 'dmm_quiz_size', 'super_soil');
    const base = `${DMM_DOSING.super_soil.rules.sizing}; ${DMM_DOSING.super_soil.rules.refresh}`;
    return sizeHint ? `${base} (${sizeHint})` : base;
  }
  // the_bundle: no per-unit dose
  return '';
}

/** Read a per-option hint (psb / ckaa / super_soil) from the chosen answer on a question. */
function optionHint(answers: DmmAnswers, field: keyof DmmAnswers, hintKey: 'psb' | 'ckaa' | 'super_soil'): string {
  const value = answers[field];
  if (!value) return '';
  const q = DMM_QUESTIONS.find((q) => q.writes_field === field);
  const opt = q?.options.find((o) => o.value === value);
  return (opt?.[hintKey] as string | undefined) || '';
}

/**
 * Resolve the final CSV sizing row for a product.
 * Why this matters: the recommendation must say what to buy and how long it
 * lasts, instead of leaving the user to infer a size from a generic dose.
 */
function sizingForProduct(
  key: ProductKey,
  answers: DmmAnswers,
  state: DmmDerivedState
): DmmProductSizing | undefined {
  const grows = arr(answers.dmm_quiz_grows);
  const lawnSize = answers.dmm_quiz_lawn_size;

  if (grows.includes('lawn') && lawnSize) {
    const lawnSizing = DMM_LAWN_SIZING[lawnSize]?.[key];
    // Why this matters: a missing specialized row must not erase valid general sizing for Bundle pillars.
    if (lawnSizing) return lawnSizing;
  }

  const signal = answers.dmm_quiz_living === 'land' ? 'big_property' : answers.dmm_quiz_size || 'few';
  const sizing = DMM_SIZING_BY_SIGNAL[signal]?.[key];

  // Keep the derived state in the signature because lawn and acreage are
  // derived display signals, even when the answer field is absent.
  if (!sizing && state.scale === 'acre') {
    return DMM_SIZING_BY_SIGNAL.big_property?.[key];
  }

  return sizing;
}

/**
 * Assemble the regimen: base from need_to_regimen[profile], then apply modifiers.
 * Purchasable products keep their URL; supplementary add-ons are surfaced separately.
 */
function assembleRegimen(profile: Profile, answers: DmmAnswers, state: DmmDerivedState): ResolvedRegimen {
  const base = DMM_NEED_TO_REGIMEN[profile.name] || ['the_bundle'];
  const setup = arr(answers.dmm_quiz_setup);
  const grows = arr(answers.dmm_quiz_grows);
  const struggle = arr(answers.dmm_quiz_struggle);
  const isLawnFocus = grows.includes('lawn');
  const isContainer = state.primary_setup === 'containers' || setup.includes('pots_outside') || setup.includes('houseplants_indoor');
  const isRaised = setup.includes('raised_beds');

  // Start from base regimen; the_bundle stays as-is (represents all three).
  let productKeys: ProductKey[] = [...base];

  // Modifier: any_container_setup -> add super_soil (if not a bundle-only regimen)
  if (isContainer && !productKeys.includes('the_bundle') && !productKeys.includes('super_soil')) {
    productKeys.push('super_soil');
  }
  // Modifier: lawn_only -> PSB hero; drop super_soil; ckaa optional (keep if present)
  if (isLawnFocus) {
    productKeys = productKeys.filter((k) => k !== 'super_soil' && k !== 'the_bundle');
    if (!productKeys.includes('plant_super_boost')) productKeys.unshift('plant_super_boost');
  }

  const toRegimenItem = (key: ProductKey): RegimenItem => {
    const meta = DMM_PRODUCTS[key];
    const sizing = sizingForProduct(key, answers, state);
    const variant = getDmmVariant(key, sizing?.sizeLabel);
    const productUrl = variant?.variantId ? `${meta.url}?variant=${variant.variantId}` : meta.url;
    return {
      key,
      name: meta.name,
      role: meta.role,
      snippet: DMM_PRODUCT_BLURBS[key] || DMM_PILLAR_SNIPPETS[key]?.short || '',
      dose: sizing?.dose || doseForProduct(key, answers, state),
      productUrl,
      ...(sizing
        ? {
            sizing: {
              ...sizing,
              ...(variant
                ? {
                    variantId: variant.variantId,
                    variantImageUrl: variant.imageUrl,
                  }
                : {}),
            },
          }
        : {}),
      ...(variant
        ? {
            variantId: variant.variantId,
            variantImageUrl: variant.imageUrl,
          }
        : {}),
    };
  };

  const items: RegimenItem[] = productKeys.map(toRegimenItem);

  // Why this matters: the Bundle remains the smart-buy bridge after every
  // personalized hero plan, so its three pillars need matching sizing data.
  const bundlePillars: RegimenItem[] = (
    ['plant_super_boost', 'crab_kelp_amino_acids', 'super_soil'] as ProductKey[]
  ).map(toRegimenItem);

  // Supplementary add-ons from regimen_modifiers. Each is enriched with its
  // Shopify handle + product URL from DMM_ADDON_PRODUCTS so the results page can
  // render them as shoppable product cards (image via the sync, CTA to the PDP).
  const addOns: RegimenAddOn[] = [];
  const addAddOn = (key: AddOnKey, name: string, note: string) => {
    if (addOns.some((a) => a.key === key)) return;
    const product = DMM_ADDON_PRODUCTS[key];
    if (!product) return;
    addOns.push({
      key,
      name,
      note: DMM_ADDON_BLURBS[key] || note,
      handle: product.handle,
      productUrl: product.url,
    });
  };
  if (isContainer) addAddOn('rice_hulls', 'Rice Hulls', 'top-dress for drainage');
  if (isRaised) {
    addAddOn('coco_coir', 'Coco Coir', 'blend into raised beds');
    addAddOn('biochar', 'Biochar', 'blend into raised beds');
    addAddOn('rice_hulls', 'Rice Hulls', 'blend into raised beds');
  }
  const bigOutdoor = state.environment === 'outdoor' && ['full_yard', 'big_property'].includes(answers.dmm_quiz_size || '');
  if (state.has_lawn || bigOutdoor) addAddOn('chameleon_sprayer', 'Chameleon Sprayer', 'hose-end sprayer that keeps microbes alive (we recommend it, we do not make it)');
  if (struggle.includes('bugs_flies')) {
    if (state.environment === 'indoor') {
      addAddOn('castile_soap', 'Castile Soap', 'a drop for fungus gnats / indoor pests');
    } else {
      addAddOn('diatomaceous_earth', 'Diatomaceous Earth', 'natural pest control');
      addAddOn('sulfur_dust', 'Sulfur Dust', 'natural pest control');
      addAddOn('castile_soap', 'Castile Soap', 'gentle pest control');
      addAddOn('chameleon_sprayer', 'Chameleon Sprayer', 'one-time hose-end sprayer');
    }
  }

  return {
    items,
    bundlePillars,
    addOns,
    generosityLine: 'You do not have to buy any of this. The field guide shows you how.',
  };
}

export interface DmmSegmentResolution {
  key?: DmmSegmentKey;
  growValue?: string;
}

/**
 * Resolve the strongest segment while preserving first-tap order within a
 * segment. Why this matters: fruit and food growers should not receive a lawn
 * reading just because lawn was selected later in a multi-select answer.
 */
export function resolveDmmSegment(grows: string[]): DmmSegmentResolution {
  for (const segment of DMM_SEGMENT_PRIORITY) {
    const firstTappedGrow = grows.find((grow) => DMM_SEGMENT_BY_GROW[grow] === segment);
    if (firstTappedGrow) {
      return { key: segment, growValue: firstTappedGrow };
    }
  }
  return {};
}

/** Select the final segment morsel from the resolved segment key. */
function pickSegmentMorsel(segment: DmmSegmentResolution): string | undefined {
  return segment.key ? DMM_SEGMENT_MORSELS[segment.key] : undefined;
}

// ===== UX-2: "because" match lines =====

interface FiredBecauseTemplates {
  /** Card-targeted templates in strength order (struggles first, per the tab). */
  cardTemplates: Array<{ card: ProductKey; template: DmmBecauseTemplate }>;
  /** Flexible why line: stack it on the hero or use the first open card slot. */
  whyTemplate: DmmBecauseTemplate | undefined;
}

/** Collect every because-line trigger that fired for this person's answers. */
function collectBecauseTemplates(
  answers: DmmAnswers,
  state: DmmDerivedState,
  profileName: string,
  segmentKey: DmmSegmentKey | undefined
): FiredBecauseTemplates {
  const struggle = arr(answers.dmm_quiz_struggle);
  const setup = arr(answers.dmm_quiz_setup);
  const cardTemplates: FiredBecauseTemplates['cardTemplates'] = [];

  for (const rule of DMM_BECAUSE_STRUGGLE) {
    if (includesAny(struggle, rule.trigger)) {
      cardTemplates.push({ card: rule.card, template: rule.template });
    }
  }

  const profileTemplate = DMM_BECAUSE_PROFILE[profileName];
  if (profileTemplate) {
    cardTemplates.push({ card: 'the_bundle', template: profileTemplate });
  }

  if (state.has_lawn && segmentKey === 'lawn') {
    cardTemplates.push({ card: 'plant_super_boost', template: DMM_BECAUSE_LAWN });
  }

  if (
    includesAny(setup, ['pots_outside', 'houseplants_indoor']) ||
    state.primary_setup === 'containers'
  ) {
    cardTemplates.push({ card: 'super_soil', template: DMM_BECAUSE_CONTAINERS });
  }

  const whyTemplate =
    answers.dmm_quiz_why === 'grow_food'
      ? DMM_BECAUSE_GROW_FOOD
      : answers.dmm_quiz_why === 'natural'
        ? DMM_BECAUSE_NATURAL
        : undefined;

  return { cardTemplates, whyTemplate };
}

/**
 * Assign one because line per card, in render order.
 * - Hero card (first, when heroStacking): combine the strongest matching
 *   trigger with the why line into one sentence (the stacking rule), fall back
 *   to the verbatim no-signal line when nothing fired.
 * - Other cards: one line each, never repeating the previous card's trigger.
 *   Cards with no signal stay quiet rather than repeating a neighbor.
 */
function assignBecauseLines(
  cards: RegimenItem[],
  fired: FiredBecauseTemplates,
  options: { heroStacking: boolean; initialPreviousId?: string }
): RegimenItem[] {
  let previousId: string | null = options.initialPreviousId ?? null;
  let whyUsed = false;

  return cards.map((item, index) => {
    const primary = fired.cardTemplates.find(
      (candidate) => candidate.card === item.key && candidate.template.id !== previousId
    );
    const isHero = index === 0 && options.heroStacking;

    if (isHero) {
      if (primary && fired.whyTemplate) {
        previousId = primary.template.id;
        whyUsed = true;
        return { ...item, becauseLine: dmmBecauseLineStacked(primary.template, fired.whyTemplate) };
      }
      if (primary) {
        previousId = primary.template.id;
        return { ...item, becauseLine: dmmBecauseLine(primary.template) };
      }
      if (fired.whyTemplate) {
        previousId = fired.whyTemplate.id;
        whyUsed = true;
        return { ...item, becauseLine: dmmBecauseLine(fired.whyTemplate) };
      }
      previousId = DMM_BECAUSE_FALLBACK.id;
      return { ...item, becauseLine: dmmBecauseLine(DMM_BECAUSE_FALLBACK) };
    }

    if (primary) {
      previousId = primary.template.id;
      return { ...item, becauseLine: dmmBecauseLine(primary.template) };
    }
    if (fired.whyTemplate && !whyUsed && fired.whyTemplate.id !== previousId) {
      previousId = fired.whyTemplate.id;
      whyUsed = true;
      return { ...item, becauseLine: dmmBecauseLine(fired.whyTemplate) };
    }
    previousId = null;
    return item;
  });
}

/**
 * Attach because lines to the regimen's visible cards: the plan items (hero
 * stacking on the first) and, for a bundle plan, the pillar cards inside it
 * (continuing the rotation from the bundle header so neighbors never repeat).
 */
export function applyDmmBecauseLines(
  regimen: ResolvedRegimen,
  answers: DmmAnswers,
  state: DmmDerivedState,
  profileName: string,
  segmentKey: DmmSegmentKey | undefined
): ResolvedRegimen {
  const fired = collectBecauseTemplates(answers, state, profileName, segmentKey);
  const items = assignBecauseLines(regimen.items, fired, { heroStacking: true });

  let bundlePillars = regimen.bundlePillars;
  if (bundlePillars && bundlePillars.length > 0) {
    const bundleTemplate = DMM_BECAUSE_PROFILE[profileName];
    bundlePillars = assignBecauseLines(bundlePillars, fired, {
      heroStacking: false,
      ...(bundleTemplate ? { initialPreviousId: bundleTemplate.id } : {}),
    });
  }

  return {
    ...regimen,
    items,
    ...(bundlePillars ? { bundlePillars } : {}),
  };
}

export interface DmmResolution {
  archetype: Archetype;
  profile: Profile;
  need: NeedKey;
  keyInsight: KeyInsight;
  needDiagnosis: string;
  painVillains: { key: string; text: string }[];
  segmentMorsel?: string | undefined;
  segmentKey?: DmmSegmentKey | undefined;
  segmentGrowValue?: string | undefined;
  segmentReview?: DmmSegmentReview | undefined;
  regimen: ResolvedRegimen;
  whyHuman: string;
}

/** Full resolution used by the results renderer. */
export function resolveDmmQuiz(answers: DmmAnswers, state: DmmDerivedState): DmmResolution {
  const archetype = resolveArchetype(answers, state);
  const profile = resolveProfile(answers);
  const need = DMM_PROFILE_NEED[profile.name] || profile.need;

  const whyTag = answers.dmm_quiz_why ? DMM_KEY_INSIGHT_BY_WHY[answers.dmm_quiz_why] : undefined;
  const keyInsight =
    DMM_KEY_INSIGHTS.find((k) => k.why_tag === whyTag) || DMM_KEY_INSIGHTS[0];

  const struggle = arr(answers.dmm_quiz_struggle);
  const painVillains = struggle
    .map((s) => {
      const opt = DMM_QUESTIONS.find((q) => q.writes_field === 'dmm_quiz_struggle')?.options.find((o) => o.value === s);
      const villainKey = opt?.villain_key || s;
      const text = DMM_PAIN_VILLAIN[villainKey];
      return text ? { key: villainKey, text } : null;
    })
    .filter((v): v is { key: string; text: string } => v !== null);

  const whyHuman = whyLabelHuman(answers.dmm_quiz_why);
  const segment = resolveDmmSegment(arr(answers.dmm_quiz_grows));
  // UX-2: every recommended card carries a because line built from their answers.
  const regimen = applyDmmBecauseLines(
    assembleRegimen(profile, answers, state),
    answers,
    state,
    profile.name,
    segment.key
  );

  return {
    archetype,
    profile,
    need,
    keyInsight,
    needDiagnosis: DMM_NEED_DIAGNOSIS[need] || DMM_NEED_DIAGNOSIS.ALL,
    painVillains,
    segmentMorsel: pickSegmentMorsel(segment),
    segmentKey: segment.key,
    segmentGrowValue: segment.growValue,
    segmentReview: segment.key ? DMM_SEGMENT_REVIEWS[segment.key] : undefined,
    regimen,
    whyHuman,
  };
}

/** Lowercased why label for the spirit line ("You grow ..."). */
function whyLabelHuman(whyValue?: string): string {
  if (!whyValue) return 'your garden';
  const q = DMM_QUESTIONS.find((q) => q.writes_field === 'dmm_quiz_why');
  const opt = q?.options.find((o) => o.value === whyValue);
  return opt ? opt.label.toLowerCase() : 'your garden';
}

// ===== Klaviyo payload (dmm_quiz_* schema) =====

/** Pre-written email merge-tag fields (USCN Option #1 pattern for DMM Welcome Flow). */
export interface DmmQuizEmailContent {
  dmm_quiz_archetype_paragraph: string;
  dmm_quiz_superpower: string;
  dmm_quiz_blind_spot: string;
  dmm_quiz_profile_bridge: string;
  dmm_quiz_key_insight: string;
  dmm_quiz_segment_morsel: string;
  dmm_quiz_product_1_snippet: string;
  dmm_quiz_product_2_snippet: string;
  dmm_quiz_product_3_snippet: string;
  dmm_quiz_product_1_snippet_long: string;
}

/**
 * Why this matters: emails get finished copy as merge tags — no fragile Klaviyo conditionals.
 * Identity rule baked in: archetype paragraph always starts with "You are {archetype}".
 */
export function buildDmmQuizEmailContent(
  answers: DmmAnswers,
  resolution: DmmResolution
): DmmQuizEmailContent {
  const grows = arr(answers.dmm_quiz_grows);
  const isLawn = grows.includes('lawn');
  const products = resolution.regimen.items.slice(0, 3);

  const snippetForProduct = (key: ProductKey | undefined, length: 'short' | 'long'): string => {
    if (!key) return '';
    // Why this matters: lawn copy overrides Super Soil while still honoring short/long email fields.
    const pillar =
      isLawn && key === 'super_soil'
        ? DMM_PILLAR_SNIPPETS.lawn_variant
        : DMM_PILLAR_SNIPPETS[key];
    if (!pillar) return '';
    if (length === 'long') return pillar.long || pillar.short || '';
    return pillar.short || '';
  };

  const profileBridge = [
    resolution.profile.headline,
    '',
    resolution.profile.description,
    '',
    `So your fix is ${resolution.profile.regimen_text}`,
  ].join('\n');

  return {
    dmm_quiz_archetype_paragraph: `You are ${resolution.archetype.name}.\n\n${resolution.archetype.core_reading}`,
    dmm_quiz_superpower: resolution.archetype.superpower,
    dmm_quiz_blind_spot: resolution.archetype.blind_spot,
    dmm_quiz_profile_bridge: profileBridge,
    dmm_quiz_key_insight: resolution.keyInsight.key_insight,
    dmm_quiz_segment_morsel: resolution.segmentMorsel || '',
    dmm_quiz_product_1_snippet: snippetForProduct(products[0]?.key, 'short'),
    dmm_quiz_product_2_snippet: snippetForProduct(products[1]?.key, 'short'),
    dmm_quiz_product_3_snippet: snippetForProduct(products[2]?.key, 'short'),
    dmm_quiz_product_1_snippet_long: snippetForProduct(products[0]?.key, 'long'),
  };
}

export interface DmmKlaviyoPayload {
  brandKey: 'drmanismagic';
  email: string;
  firstName: string;
  zipCode: string;
  quizProfile: string;
  quizProfileId?: string | undefined;
  quizArchetype?: string | undefined;
  quizExperience?: string | undefined;
  recommendedTrees: { id: string; name: string; productUrl: string; imageUrl: string }[];
  tags: string[];
  answers: Record<string, string[]>;
  klaviyoProperties: Record<string, unknown>;
  klaviyoEventProperties: Record<string, unknown>;
  klaviyoEventName: string;
}

/**
 * Build the submit payload for POST /api/quiz/submit with brandKey 'drmanismagic'.
 * The backend passes klaviyoProperties / klaviyoEventProperties straight through to
 * Klaviyo, so this is the authoritative dmm_quiz_* schema (spec: logic.field_map).
 */
export function buildDmmKlaviyoPayload(
  answers: DmmAnswers,
  resolution: DmmResolution,
  email: string,
  zip: string
): DmmKlaviyoPayload {
  const struggle = arr(answers.dmm_quiz_struggle);
  const grows = arr(answers.dmm_quiz_grows);
  const setup = arr(answers.dmm_quiz_setup);

  const products = resolution.regimen.items.slice(0, 3);
  const productFields: Record<string, string> = {
    dmm_product_1: products[0]?.name || '',
    dmm_product_2: products[1]?.name || '',
    dmm_product_3: products[2]?.name || '',
  };

  // Why this matters: pre-written email fields ride with metadata so Welcome Flow uses merge tags.
  const emailContent = buildDmmQuizEmailContent(answers, resolution);

  const klaviyoProperties: Record<string, unknown> = {
    dmm_quiz_completed: true,
    dmm_quiz_living: answers.dmm_quiz_living || '',
    dmm_quiz_grows: grows.join(', '),
    dmm_quiz_setup: setup.join(', '),
    dmm_quiz_lawn_size: answers.dmm_quiz_lawn_size || '',
    dmm_quiz_size: answers.dmm_quiz_size || '',
    dmm_quiz_why: answers.dmm_quiz_why || '',
    dmm_quiz_struggle: struggle.join(', '),
    dmm_quiz_experience: answers.dmm_quiz_experience || '',
    dmm_quiz_proudest: answers.dmm_quiz_proudest || '',
    dmm_quiz_zip: zip,
    // Why this matters: Content Blocks / Klaviyo conditionals key on snake_case ids, not display names.
    dmm_quiz_archetype: resolution.archetype.id,
    dmm_quiz_profile: resolution.profile.id,
    dmm_quiz_archetype_name: resolution.archetype.name,
    dmm_quiz_profile_name: resolution.profile.name,
    dmm_quiz_need: resolution.need,
    ...productFields,
    ...emailContent,
    dmm_quiz_pain_tag: resolution.profile.pain_tag,
    dmm_quiz_date: new Date().toISOString(),
  };

  const klaviyoEventProperties: Record<string, unknown> = {
    ...klaviyoProperties,
    answers,
    recommended_products: resolution.regimen.items.map((i) => i.name),
    add_ons: resolution.regimen.addOns.map((a) => a.name),
    timestamp: new Date().toISOString(),
  };

  return {
    brandKey: 'drmanismagic',
    email,
    firstName: '',
    zipCode: zip,
    quizProfile: resolution.profile.name,
    quizProfileId: resolution.profile.id,
    quizArchetype: resolution.archetype.name,
    quizExperience: answers.dmm_quiz_experience,
    recommendedTrees: resolution.regimen.items.map((i) => ({
      id: i.key,
      name: i.name,
      productUrl: i.productUrl,
      imageUrl: '',
    })),
    tags: [resolution.archetype.name, resolution.profile.name, resolution.need, DMM_OFFER.quiz_code],
    answers: {
      dmm_quiz_living: arr(answers.dmm_quiz_living ? [answers.dmm_quiz_living] : []),
      dmm_quiz_grows: grows,
      dmm_quiz_setup: setup,
      dmm_quiz_struggle: struggle,
      dmm_quiz_why: arr(answers.dmm_quiz_why ? [answers.dmm_quiz_why] : []),
      dmm_quiz_experience: arr(answers.dmm_quiz_experience ? [answers.dmm_quiz_experience] : []),
    },
    klaviyoProperties,
    klaviyoEventProperties,
    klaviyoEventName: 'Dr Mani Quiz Completed',
  };
}
